import { useDispatch, useSelector } from "react-redux"
import React, { useEffect, useState, useRef } from "react"
import {
  TASpamBrand,
  TAbalanceLoaded,
  TAbrandECommerce,
  TAbrandHeardFrom,
  TAbrandSalesReadiness,
  TAbrandSize,
  TAcampaignCompleted,
  TAcampaignManagementContinues,
  TAdemoCampaignCreated,
  TAdirectedToAgencyBasedWork,
  TAfindAllBrands,
  TAinformationMailSent,
  TAmeetingPlanned,
  TApersonalAccount,
  TAupdateNote,
  TAdeleteNote,
  TAupdateBrandMeeting,
  TAdeleteBrandMeeting,
  TAupdateBrandStage,
  TAsetResponsibleAdmin,
  TAgetCampaignNotes,
  TAbrandSetCpmAim,
  TAbrandSetLeadStyle,
  TAbrandSetUtm,
  TAbrandReferenceChange,
  TAsetBrandClassification,
} from "../../../services/brandAPI"
import { selectUser } from "../../../redux/store/auth/authSlice"
import { DataTableSortStatus } from "mantine-datatable"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import { Brand, Meeting, Note, Stage } from "../../../types/brandDatas"
import { columnConfig } from "./components/columns"

import { TAgetQuizzes } from "../../../services/quizAPI"
import { useNavigate } from "react-router-dom"
import Filters from "./components/Filters"
import NotesModal from "./components/NotesModal"
import MeetingModal from "./components/MeetingModal"
import BrandOperationsModal from "./components/BrandOperationsModal"
import StagesModal from "./components/StagesModal"
import EcommerceModal from "./components/EcommerceModal"
import BrandSizeModal from "./components/BrandSizeModal"
import BrandStateHistoryModal from "./components/BrandStateHistoryModal"
import ResponsibleAdminModal from "./components/ResponsibleAdminModal"
import CpmAimModal from "./components/CpmAimModal"
import LeadStyleModal from "./components/LeadStyleModal"
import UtmModal from "./components/UtmModal"
import GroupedColumns from "./components/GroupedColumns"
import StickyDataTable from "../../../components/StickyDataTable"
import ReferenceChangeModal from "./components/ReferenceChangeModal"
import {
  BrandColumn,
  BrandListApiResponse,
  BrandListFilters,
  BrandListRecord,
  BrandOperationKey,
  BrandOperationsState,
  BrandStateHistoryItem,
  CampaignNote,
  MeetingPayload,
  Quiz,
} from "./types"
import { getErrorStatus, getErrorMessage, isAbortError } from "../../../types/error"
import CategoryModal from "./components/CategoryModal"
import ClassificationModal, { Classification } from "./components/ClassificationModal"

const defaultBrandOperationsState: BrandOperationsState = {
  meeting_planned: false,
  information_mail_sent: false,
  directed_to_agency_based_work: false,
  demo_campaign_created: false,
  balance_loaded: false,
  campaign_management_continues: false,
  campaign_completed: false,
  personal_account: false,
}

const AllBrands = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""
  const abortControllerRef = useRef<AbortController | null>(null)

  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZES[2])
  const [initialRecords, setInitialRecords] = useState<BrandListRecord[]>([])
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor: "id",
    direction: "asc",
  })
  const [error, setError] = useState<string | null>(null)
  const [sortByColumn, setSortByColumn] = useState("")
  const [sortOrder, setSortOrder] = useState(true)
  const [loading, setLoading] = useState(false)
  const [editRateId, setEditRateId] = useState<string | null>(null)
  const [rateValues, setRateValues] = useState<Record<string, number | undefined>>({})
  const [quizzes, setQuizzes] = useState<Quiz[]>([])
  const [heardFromValues, setHeardFromValues] = useState<Record<string, string>>({})
  const [editHeardFromId, setEditHeardFromId] = useState<string | null>(null)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  // Column group states
  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({
    identity: true,
    financial: false,
    campaign: false,
    application: false,
    notes: false,
    marketing: false,
    quiz: false,
  })

  // Modal states
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false)
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false)
  const [isOperationsModalOpen, setIsOperationsModalOpen] = useState(false)
  const [isStagesModalOpen, setIsStagesModalOpen] = useState(false)
  const [isEcommerceModalOpen, setIsEcommerceModalOpen] = useState(false)
  const [isBrandSizeModalOpen, setIsBrandSizeModalOpen] = useState(false)
  const [isBrandStateHistoryModalOpen, setIsBrandStateHistoryModalOpen] = useState(false)
  const [isResponsibleAdminModalOpen, setIsResponsibleAdminModalOpen] = useState(false)
  const [isCpmAimModalOpen, setIsCpmAimModalOpen] = useState(false)
  const [isLeadStyleModalOpen, setIsLeadStyleModalOpen] = useState(false)
  const [isUtmModalOpen, setIsUtmModalOpen] = useState(false)
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false)
  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const [selectedBrandNotes, setSelectedBrandNotes] = useState<Note[]>([])
  const [selectedBrandMeetings, setSelectedBrandMeetings] = useState<Meeting[]>([])
  const [selectedCampaignNotes, setSelectedCampaignNotes] = useState<CampaignNote[]>([])
  const [selectedBrandStages, setSelectedBrandStages] = useState<Stage[]>([])
  const [selectedEcommerceValue, setSelectedEcommerceValue] = useState<string>("")
  const [selectedBrandSize, setSelectedBrandSize] = useState<string>("")
  const [selectedBrandName, setSelectedBrandName] = useState<string>("")
  const [selectedBrandStateHistory, setSelectedBrandStateHistory] = useState<
    BrandStateHistoryItem[]
  >([])
  const [selectedResponsibleAdmins, setSelectedResponsibleAdmins] = useState<string[]>([])
  const [selectedCpmAim, setSelectedCpmAim] = useState<number>(0)
  const [selectedLeadStyle, setSelectedLeadStyle] = useState<
    "dominance" | "influencing" | "cautious" | "stabilizing" | ""
  >("")
  const [selectedUtm, setSelectedUtm] = useState<Brand["utm"]>({})
  const [selectedBrandOperations, setSelectedBrandOperations] = useState<BrandOperationsState>(
    defaultBrandOperationsState,
  )
  const [selectedBrandReference, setSelectedBrandReference] = useState<string>("")
  const [isOpenCategoryModal, setIsOpenCategoryModal] = useState(false)
  const [isOpenClassificationModal, setIsOpenClassificationModal] = useState(false)
  const [selectedClassification, setSelectedClassification] = useState<Classification | null>(null)

  const [params, setParams] = useState<BrandListFilters>({
    min_created_at: null,
    max_created_at: null,
    min_last_login: null,
    max_last_login: null,
    brand_name: "",
    email: "",
    first_name: "",
    last_name: "",
    active_campaigns: "",
    stage: [""],
    brand_state: [""],
    responsible_admin: [],
    process: {
      meeting_planned: "",
      information_mail_sent: "",
      directed_to_agency_based_work: "",
      demo_campaign_created: "",
      balance_loaded: "",
      campaign_management_continues: "",
      campaign_completed: "",
      personal_account: "",
    },
    classification: "",
    reference: "",
  })

  useEffect(() => {
    dispatch(setPageTitle("Tüm Markalar"))
  })

  useEffect(() => {
    handleFetchData()
    getQuizzes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByColumn, sortOrder, page, pageSize, params])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    setPage(1)
  }, [pageSize, params, sortByColumn, sortOrder])

  useEffect(() => {
    setSortStatus({
      columnAccessor: sortByColumn || "id",
      direction: sortOrder ? "desc" : "asc",
    })
  }, [sortByColumn, sortOrder])

  const handleSorting = (columnAccessor: string, direction?: "asc" | "desc") => {
    if (columnAccessor === sortByColumn) {
      setSortOrder(direction === "desc")
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(direction === "desc")
    }
  }

  const handleFetchData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const newParams = {
        min_created_at: params.min_created_at ? params.min_created_at.toISOString() : "",
        max_created_at: params.max_created_at ? params.max_created_at.toISOString() : "",
        min_last_login: params.min_last_login ? params.min_last_login.toISOString() : "",
        max_last_login: params.max_last_login ? params.max_last_login.toISOString() : "",
        brand_name: params.brand_name,
        email: params.email,
        first_name: params.first_name,
        last_name: params.last_name,
        active_campaigns: params.active_campaigns,
        stage: params.stage.join(";"),
        brand_state: params.brand_state.join(";"),
        responsible_admin: params.responsible_admin.join(";"),
        meeting_planned: params.process.meeting_planned.toString(),
        information_mail_sent: params.process.information_mail_sent.toString(),
        directed_to_agency_based_work: params.process.directed_to_agency_based_work.toString(),
        demo_campaign_created: params.process.demo_campaign_created.toString(),
        balance_loaded: params.process.balance_loaded.toString(),
        campaign_management_continues: params.process.campaign_management_continues.toString(),
        campaign_completed: params.process.campaign_completed.toString(),
        personal_account: params.process.personal_account.toString(),
        classification: params.classification,
        reference: params.reference,
      }
      const queryParams = new URLSearchParams(newParams as Record<string, string>).toString()
      const response = (await TAfindAllBrands(
        sortByColumn,
        sortOrder,
        page,
        pageSize,
        queryParams,
        abortControllerRef.current.signal,
      )) as BrandListApiResponse
      if (response && Array.isArray(response.brands)) {
        const totalLength = response.brands.length
        const totalDocumentsCount = response.totalDocuments
        const data = response.brands.map((item, index) => {
          return {
            ...item,
            id: totalLength - index,
          }
        })

        setInitialRecords(data)
        setTotalRecords(totalDocumentsCount)
        setLoading(false)
      } else {
        setLoading(false)
        setError("No data found")
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setLoading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }

      setLoading(false)
      setError("Error fetching data")
    }
  }

  const getQuizzes = async () => {
    try {
      const response = (await TAgetQuizzes("tr")) as Quiz[]
      if (response && Array.isArray(response)) {
        setQuizzes(response)
      } else {
        setError("No quizzes found")
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error fetching data, ${getErrorMessage(error)}`)
    }
  }

  const handleUpdateNote = async (brandId: string, note?: string) => {
    try {
      const updatedBrand = await TAupdateNote(brandId, userEmail, note)
      setInitialRecords((prev) =>
        prev.map((brand) =>
          brand._id === brandId ? { ...brand, notes: updatedBrand.notes || [] } : brand,
        ),
      )
      return (updatedBrand?.notes ?? []) as Note[]
    } catch (error: unknown) {
      const status = getErrorStatus(error)
      if (status === 401) {
        navigate("/login")
      }
      setError(`Error updating note, ${getErrorMessage(error)}`)
      return undefined
    }
  }

  const handleDeleteNote = async (_id: string, index: number) => {
    try {
      await TAdeleteNote(_id, index)
      const updatedBrands = initialRecords.map((brand) => {
        if (brand._id === _id) {
          return {
            ...brand,
            notes: brand.notes?.filter((_, i) => i !== index) || [],
          }
        }
        return brand
      })
      setInitialRecords([...updatedBrands])
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error deleting note, ${getErrorMessage(error)}`)
    }
  }

  const handleECommerce = async (_id: string, ecommerce: string) => {
    try {
      const response = await TAbrandECommerce(_id, ecommerce)
      if (response) {
        setInitialRecords((prevRecords) =>
          prevRecords.map((record) =>
            record._id === _id
              ? {
                  ...record,
                  customer_success_input: {
                    ...(record.customer_success_input || {}),
                    e_commerce_infrastructure: ecommerce,
                  },
                }
              : record,
          ),
        )
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating e-commerce, ${getErrorMessage(error)}`)
    }
  }

  const handleBrandSize = async (_id: string, size: string) => {
    try {
      const response = await TAbrandSize(_id, size)
      if (response) {
        setInitialRecords((prevRecords) =>
          prevRecords.map((record) =>
            record._id === _id ? { ...record, brand_size: size as Brand["brand_size"] } : record,
          ),
        )
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating brand size, ${getErrorMessage(error)}`)
    }
  }

  const handleBrandStage = async (_id: string, stages: Stage[]) => {
    try {
      await TAupdateBrandStage(_id, stages)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) => (record._id === _id ? { ...record, stages: stages } : record)),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating stage, ${getErrorMessage(error)}`)
    }
  }

  const handleMeetingPlanned = async (_id: string, status: boolean) => {
    try {
      await TAmeetingPlanned(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, meeting_planned: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating meeting planned, ${getErrorMessage(error)}`)
    }
  }

  const handleInformationMailSent = async (_id: string, status: boolean) => {
    try {
      await TAinformationMailSent(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, information_mail_sent: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating information mail sent, ${getErrorMessage(error)}`)
    }
  }

  const handleDirectedToAgencyBasedWork = async (_id: string, status: boolean) => {
    try {
      await TAdirectedToAgencyBasedWork(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, directed_to_agency_based_work: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating directed to agency based work, ${getErrorMessage(error)}`)
    }
  }

  const handleDemoCampaignCreated = async (_id: string, status: boolean) => {
    try {
      await TAdemoCampaignCreated(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, demo_campaign_created: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating demo campaign created, ${getErrorMessage(error)}`)
    }
  }

  const handleBalanceLoaded = async (_id: string, status: boolean) => {
    try {
      await TAbalanceLoaded(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, balance_loaded: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating balance loaded, ${getErrorMessage(error)}`)
    }
  }

  const handleCampaignManagementContinues = async (_id: string, status: boolean) => {
    try {
      await TAcampaignManagementContinues(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, campaign_management_continues: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating campaign management continues, ${getErrorMessage(error)}`)
    }
  }

  const handleCampaignCompleted = async (_id: string, status: boolean) => {
    try {
      await TAcampaignCompleted(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, campaign_completed: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating campaign completed, ${getErrorMessage(error)}`)
    }
  }

  const handlePersonalAccount = async (_id: string, status: boolean) => {
    try {
      await TApersonalAccount(_id, status)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) =>
          record._id === _id ? { ...record, personal_account: status } : record,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating personal account, ${getErrorMessage(error)}`)
    }
  }

  const handleSpamBrand = async (_id: string) => {
    try {
      await TASpamBrand(_id, "true")
      setInitialRecords((prevRecords) => prevRecords.filter((record) => record._id !== _id))
      setTotalRecords((prevTotal) => prevTotal - 1)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error marking brand as spam, ${getErrorMessage(error)}`)
    }
  }

  const handleSalesReadiness = async (_id: string, rate: number) => {
    try {
      await TAbrandSalesReadiness(_id, rate)
      setInitialRecords((prev) =>
        prev.map((brand) =>
          brand._id === _id
            ? {
                ...brand,
                customer_success_input: { ...brand.customer_success_input, sales_readiness: rate },
              }
            : brand,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating sales readiness, ${getErrorMessage(error)}`)
    }
  }

  const handleHeardFrom = async (_id: string, heardFrom: string) => {
    try {
      await TAbrandHeardFrom(_id, heardFrom)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, heardFrom: heardFrom } : brand)),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating heard from, ${getErrorMessage(error)}`)
    }
  }

  const handleCpmAim = async (_id: string, cpmAim: number) => {
    try {
      await TAbrandSetCpmAim(_id, cpmAim)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, cpm_aim: cpmAim } : brand)),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating CPM Aim, ${getErrorMessage(error)}`)
    }
  }

  const handleLeadStyle = async (
    _id: string,
    leadStyle: "dominance" | "influencing" | "cautious" | "stabilizing",
  ) => {
    try {
      await TAbrandSetLeadStyle(_id, leadStyle)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, lead_style: leadStyle } : brand)),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating Lead Style, ${getErrorMessage(error)}`)
    }
  }

  const handleUtmUpdate = async (
    _id: string,
    utm: { utm_source?: string; utm_medium?: string; utm_campaign?: string },
  ) => {
    try {
      await TAbrandSetUtm(_id, utm)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, utm: utm } : brand)),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating UTM parameters, ${getErrorMessage(error)}`)
    }
  }

  const onOpenNotesModal = async (brandId: string, notes: Note[]) => {
    setSelectedBrandId(brandId)
    setSelectedBrandNotes(notes)

    try {
      const campaignNotes = (await TAgetCampaignNotes(brandId)) as CampaignNote[]
      setSelectedCampaignNotes(campaignNotes || [])
    } catch (error: unknown) {
      setSelectedCampaignNotes([])
    }

    setIsNotesModalOpen(true)
  }

  const onOpenMeetingModal = (brandId: string, meetings: Meeting[] = []) => {
    setSelectedBrandId(brandId)
    setSelectedBrandMeetings(meetings || [])
    setIsMeetingModalOpen(true)
  }

  const handleAddMeeting = async (brandId: string, meeting: MeetingPayload) => {
    try {
      const response = await TAupdateBrandMeeting(brandId, meeting, userEmail)
      if (response) {
        const updatedMeetings = (response.meetings || []) as Meeting[]
        setInitialRecords((prev) =>
          prev.map((brand) =>
            brand._id === brandId ? { ...brand, meetings: updatedMeetings } : brand,
          ),
        )
        setSelectedBrandMeetings(updatedMeetings)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error adding meeting, ${getErrorMessage(error)}`)
    }
  }

  const handleDeleteMeeting = async (brandId: string, index: number) => {
    try {
      const response = await TAdeleteBrandMeeting(brandId, index)
      if (response) {
        const updatedMeetings = (response.meetings || []) as Meeting[]
        setInitialRecords((prev) =>
          prev.map((brand) =>
            brand._id === brandId ? { ...brand, meetings: updatedMeetings } : brand,
          ),
        )
        setSelectedBrandMeetings(updatedMeetings)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error deleting meeting, ${getErrorMessage(error)}`)
    }
  }

  const handleReferenceChange = async (_id: string, reference: string) => {
    try {
      await TAbrandReferenceChange(_id, reference)
      setInitialRecords((prev) =>
        prev.map((brand) => (brand._id === _id ? { ...brand, reference } : brand)),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating reference, ${getErrorMessage(error)}`)
    }
  }

  const onOpenOperationsModal = (brandId: string, operations: BrandOperationsState) => {
    setSelectedBrandId(brandId)
    setSelectedBrandOperations({ ...defaultBrandOperationsState, ...operations })
    setIsOperationsModalOpen(true)
  }

  const onOpenStagesModal = (brandId: string, stages: Stage[]) => {
    setSelectedBrandId(brandId)
    setSelectedBrandStages(stages)
    setIsStagesModalOpen(true)
  }

  const onOpenEcommerceModal = (brandId: string, currentValue: string) => {
    setSelectedBrandId(brandId)
    setSelectedEcommerceValue(currentValue)
    setIsEcommerceModalOpen(true)
  }

  const onOpenBrandSizeModal = (brandId: string, currentSize: string) => {
    setSelectedBrandId(brandId)
    setSelectedBrandSize(currentSize)
    setIsBrandSizeModalOpen(true)
  }

  const onOpenBrandStateHistoryModal = (
    brandId: string,
    brandName: string,
    brandStateHistory: BrandStateHistoryItem[] = [],
  ) => {
    setSelectedBrandId(brandId)
    setSelectedBrandName(brandName)
    setSelectedBrandStateHistory(brandStateHistory)
    setIsBrandStateHistoryModalOpen(true)
  }

  const onOpenResponsibleAdminModal = (brandId: string, currentAdmins?: string[]) => {
    setSelectedBrandId(brandId)
    setSelectedResponsibleAdmins(currentAdmins || [])
    setIsResponsibleAdminModalOpen(true)
  }

  const onOpenCpmAimModal = (brandId: string, currentValue?: number) => {
    setSelectedBrandId(brandId)
    setSelectedCpmAim(currentValue || 0)
    setIsCpmAimModalOpen(true)
  }

  const onOpenLeadStyleModal = (
    brandId: string,
    currentValue?: "dominance" | "influencing" | "cautious" | "stabilizing",
  ) => {
    setSelectedBrandId(brandId)
    setSelectedLeadStyle(currentValue || "")
    setIsLeadStyleModalOpen(true)
  }

  const onOpenUtmModal = (brandId: string, currentUtm?: Brand["utm"]) => {
    setSelectedBrandId(brandId)
    setSelectedUtm(currentUtm || {})
    setIsUtmModalOpen(true)
  }

  const onOpenReferenceModal = (brandId: string, reference?: string) => {
    setSelectedBrandId(brandId)
    setSelectedBrandReference(reference || "")
    setIsReferenceModalOpen(true)
  }

  const onOpenClassificationModal = (
    brandId: string,
    currentClassification?: Classification | null,
  ) => {
    setSelectedBrandId(brandId)
    // Ensure we have a valid classification array or null
    if (
      currentClassification &&
      Array.isArray(currentClassification) &&
      currentClassification.length > 0
    ) {
      setSelectedClassification(currentClassification)
    } else {
      setSelectedClassification(null)
    }
    setIsOpenClassificationModal(true)
  }

  const handleClassificationChange = async (brandId: string, classification: Classification) => {
    try {
      // Transform classification data to match backend format
      // Backend expects: main_category (String) and sub_categories (Array of { name: String, confidence: Number })
      // Modal uses: main_category: { category: string, confidence: number } and sub_categories: [{ category: string, confidence: number }]

      if (!classification || classification.length === 0) {
        // If no classification selected, send empty values
        await TAsetBrandClassification(brandId, "", [])
        setInitialRecords((prev) =>
          prev.map((brand) =>
            brand._id === brandId ? { ...brand, main_category: "", sub_categories: [] } : brand,
          ),
        )
        return
      }

      // Take the first main_category (backend only accepts one)
      const mainCategory = classification[0]?.main_category?.category || ""

      // Collect all unique sub_categories from all selected classifications
      const subCategoriesMap = new Map<string, number>()
      classification.forEach((item) => {
        item.sub_categories?.forEach((sub) => {
          if (sub.category) {
            // Use the highest confidence if duplicate
            const existing = subCategoriesMap.get(sub.category)
            if (!existing || sub.confidence > existing) {
              subCategoriesMap.set(sub.category, sub.confidence)
            }
          }
        })
      })

      const subCategories = Array.from(subCategoriesMap.entries()).map(([name, confidence]) => ({
        name,
        confidence,
      }))

      await TAsetBrandClassification(brandId, mainCategory, subCategories)

      setInitialRecords((prev) =>
        prev.map((brand) =>
          brand._id === brandId
            ? {
                ...brand,
                main_category: mainCategory,
                sub_categories: subCategories,
              }
            : brand,
        ),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating classification, ${getErrorMessage(error)}`)
    }
  }

  const handleAssignResponsibleAdmin = async (brandId: string, adminEmails: string[]) => {
    try {
      await TAsetResponsibleAdmin(brandId, adminEmails)
      setInitialRecords((prevRecords) =>
        prevRecords.map((record) => {
          if (record._id === brandId) {
            return { ...record, responsible_admin: adminEmails }
          }
          return record
        }),
      )
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleOperationChange = async (operation: BrandOperationKey, value: boolean) => {
    setSelectedBrandOperations((prev) => ({ ...prev, [operation]: value }))

    setInitialRecords((prev) =>
      prev.map((brand) =>
        brand._id === selectedBrandId ? { ...brand, [operation]: value } : brand,
      ),
    )

    try {
      switch (operation) {
        case "meeting_planned":
          await handleMeetingPlanned(selectedBrandId, value)
          break
        case "information_mail_sent":
          await handleInformationMailSent(selectedBrandId, value)
          break
        case "directed_to_agency_based_work":
          await handleDirectedToAgencyBasedWork(selectedBrandId, value)
          break
        case "demo_campaign_created":
          await handleDemoCampaignCreated(selectedBrandId, value)
          break
        case "balance_loaded":
          await handleBalanceLoaded(selectedBrandId, value)
          break
        case "campaign_management_continues":
          await handleCampaignManagementContinues(selectedBrandId, value)
          break
        case "campaign_completed":
          await handleCampaignCompleted(selectedBrandId, value)
          break
        case "personal_account":
          await handlePersonalAccount(selectedBrandId, value)
          break
      }
    } catch (error: unknown) {
      setError(`Error updating ${operation}: ${getErrorMessage(error)}`)
    }
  }

  // Column group toggle handler
  const handleGroupToggle = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  // Get column configuration
  const columnGroups = columnConfig({
    handleSpamBrand,
    quizzes,
    handleSalesReadiness,
    handleHeardFrom,
    editRateId,
    setEditRateId,
    rateValues,
    setRateValues,
    editHeardFromId,
    setEditHeardFromId,
    heardFromValues,
    setHeardFromValues,
    onOpenNotesModal,
    onOpenMeetingModal,
    onOpenOperationsModal,
    onOpenStagesModal,
    onOpenEcommerceModal,
    onOpenBrandSizeModal,
    onOpenBrandStateHistoryModal,
    onOpenResponsibleAdminModal,
    onOpenCpmAimModal,
    onOpenLeadStyleModal,
    onOpenUtmModal,
    onOpenReferenceModal,
    onOpenClassificationModal,
  })

  // Build final columns array based on expanded groups
  const getFinalColumns = (): BrandColumn[] => {
    const finalColumns: BrandColumn[] = []

    // Add identity columns (always visible)
    if (expandedGroups.identity) {
      finalColumns.push(...columnGroups.identityColumns)
    }

    // Add financial columns
    if (expandedGroups.financial) {
      finalColumns.push(...columnGroups.financialColumns)
    }

    // Add notes columns
    if (expandedGroups.notes) {
      finalColumns.push(...columnGroups.notesColumns)
    }

    // Add marketing columns
    if (expandedGroups.marketing) {
      finalColumns.push(...columnGroups.marketingColumns)
    }

    // Add campaign columns
    if (expandedGroups.campaign) {
      finalColumns.push(...columnGroups.campaignColumns)
    }

    // Add application columns
    if (expandedGroups.application) {
      finalColumns.push(...columnGroups.applicationColumns)
    }

    // Add quiz columns
    if (expandedGroups.quiz) {
      finalColumns.push(...columnGroups.quizColumns)
    }

    return finalColumns
  }

  // Prepare groups for the GroupedColumns component
  const groups = [
    {
      title: "Kimlik & Temel Bilgiler",
      columns: columnGroups.identityColumns,
      isExpanded: expandedGroups.identity,
    },
    {
      title: "Bütçe / Finansal Durum",
      columns: columnGroups.financialColumns,
      isExpanded: expandedGroups.financial,
    },
    {
      title: "Ek Notlar ve İletişim Geçmişi",
      columns: columnGroups.notesColumns,
      isExpanded: expandedGroups.notes,
    },
    {
      title: "Pazarlama & Strateji Bilgileri",
      columns: columnGroups.marketingColumns,
      isExpanded: expandedGroups.marketing,
    },
    {
      title: "Kampanya Verileri",
      columns: columnGroups.campaignColumns,
      isExpanded: expandedGroups.campaign,
    },
    {
      title: "Başvuru Verileri",
      columns: columnGroups.applicationColumns,
      isExpanded: expandedGroups.application,
    },
    {
      title: "Quiz Soruları",
      columns: columnGroups.quizColumns,
      isExpanded: expandedGroups.quiz,
    },
  ]

  const columns = getFinalColumns()

  return (
    <div className="panel">
      <div className="mb-4.5 flex md:items-center md:flex-row flex-col gap-5">
        {error && (
          <div className="bg-red-200 text-red-800 border border-red-600 p-2 rounded">{error}</div>
        )}
        <div
          className="flex justify-center p-2 items-center border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-100 md:w-1/10"
          onClick={() => setIsOpenCategoryModal(true)}
        >
          <span>{params.classification ? params.classification : "Sınıflandırma Filtresi"}</span>
        </div>
        <div className="ltr:ml-auto rtl:mr-auto flex items-center gap-4">
          <button onClick={() => setIsFiltersOpen(true)} className="btn btn-primary">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filtrele
          </button>
        </div>
      </div>

      <CategoryModal
        title="Markaları Filtrele"
        isOpen={isOpenCategoryModal}
        onClose={() => setIsOpenCategoryModal(false)}
        onSelect={(classification) => {
          setParams({ ...params, classification: classification })
        }}
        selectedValue={{ category: params.classification }}
      />

      <Filters
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        handleFetchData={handleFetchData}
        params={params}
        setParams={setParams}
      />

      <div className="datatables">
        {/* Column Groups - Outside the scrollable area */}
        <GroupedColumns
          groups={groups}
          onGroupToggle={(groupIndex) => {
            const groupKeys = [
              "identity",
              "financial",
              "notes",
              "marketing",
              "campaign",
              "application",
              "quiz",
            ]
            handleGroupToggle(groupKeys[groupIndex])
          }}
        />

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <StickyDataTable
            records={initialRecords}
            columns={columns}
            totalRecords={totalRecords}
            recordsPerPage={pageSize}
            page={page}
            onPageChange={(p) => setPage(p)}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={setPageSize}
            sortStatus={sortStatus}
            onSortStatusChange={(e) => handleSorting(e.columnAccessor, e.direction)}
            minHeight={200}
            paginationText={({ from, to, totalRecords }) =>
              `Showing  ${from} to ${to} of ${totalRecords} entries`
            }
            highlightOnHover={true}
            className="whitespace-nowrap"
          />
        )}
      </div>

      {/* Modals */}
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        brandId={selectedBrandId}
        notes={selectedBrandNotes}
        campaignNotes={selectedCampaignNotes}
        onAddNote={handleUpdateNote}
        onDeleteNote={handleDeleteNote}
      />

      <MeetingModal
        isOpen={isMeetingModalOpen}
        onClose={() => setIsMeetingModalOpen(false)}
        brandId={selectedBrandId}
        meetings={selectedBrandMeetings}
        onAddMeeting={handleAddMeeting}
        onDeleteMeeting={handleDeleteMeeting}
      />

      <BrandOperationsModal
        isOpen={isOperationsModalOpen}
        onClose={() => setIsOperationsModalOpen(false)}
        brandId={selectedBrandId}
        operations={selectedBrandOperations}
        onOperationChange={handleOperationChange}
      />

      <StagesModal
        isOpen={isStagesModalOpen}
        onClose={() => setIsStagesModalOpen(false)}
        brandId={selectedBrandId}
        stages={selectedBrandStages}
        onStageChange={handleBrandStage}
      />

      <EcommerceModal
        isOpen={isEcommerceModalOpen}
        onClose={() => setIsEcommerceModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedEcommerceValue}
        onSave={handleECommerce}
      />

      <BrandSizeModal
        isOpen={isBrandSizeModalOpen}
        onClose={() => setIsBrandSizeModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedBrandSize}
        onSave={handleBrandSize}
      />

      <BrandStateHistoryModal
        isOpen={isBrandStateHistoryModalOpen}
        onClose={() => setIsBrandStateHistoryModalOpen(false)}
        brandId={selectedBrandId}
        brandName={selectedBrandName}
        brandStateHistory={selectedBrandStateHistory}
      />

      <ResponsibleAdminModal
        isOpen={isResponsibleAdminModalOpen}
        onClose={() => setIsResponsibleAdminModalOpen(false)}
        brandId={selectedBrandId}
        currentAdmins={selectedResponsibleAdmins}
        onAssign={handleAssignResponsibleAdmin}
      />

      <CpmAimModal
        isOpen={isCpmAimModalOpen}
        onClose={() => setIsCpmAimModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedCpmAim}
        onSave={handleCpmAim}
      />

      <LeadStyleModal
        isOpen={isLeadStyleModalOpen}
        onClose={() => setIsLeadStyleModalOpen(false)}
        brandId={selectedBrandId}
        currentValue={selectedLeadStyle || undefined}
        onSave={handleLeadStyle}
      />

      <UtmModal
        isOpen={isUtmModalOpen}
        onClose={() => setIsUtmModalOpen(false)}
        brandId={selectedBrandId}
        currentUtm={selectedUtm}
        onUpdateUtm={handleUtmUpdate}
      />

      <ReferenceChangeModal
        isOpen={isReferenceModalOpen}
        onClose={() => setIsReferenceModalOpen(false)}
        brandId={selectedBrandId}
        brandReference={selectedBrandReference}
        onReferenceChange={handleReferenceChange}
      />

      <ClassificationModal
        title="Marka Sınıflandırma"
        isOpen={isOpenClassificationModal}
        onClose={() => setIsOpenClassificationModal(false)}
        onSelect={() => handleClassificationChange(selectedBrandId, selectedClassification || [])}
        selectedClassification={selectedClassification}
        setSelectedClassification={setSelectedClassification}
      />
    </div>
  )
}

export default AllBrands
